/**
 * Progress Sync Service
 * Provides offline-aware progress syncing with automatic queue fallback
 */
import { queueSyncItem, getSyncQueue } from '@/lib/sync';

export interface SaveProgressOptions {
  endpoint?: string;
}

export interface SaveProgressResult {
  status: 'synced' | 'queued' | 'failed';
  data?: any;
  id?: number;
  reason?: string;
  error?: string;
}

/**
 * Save progress with automatic online/offline handling
 */
export async function saveProgress(
  progressData: any, 
  options: SaveProgressOptions = {}
): Promise<SaveProgressResult> {
  const endpoint = options.endpoint || '/api/progress';
  const isOnline = typeof navigator !== 'undefined' && navigator.onLine;
  
  if (!isOnline) {
    try {
      const id = await queueSyncItem('progress', progressData);
      return { status: 'queued', id };
    } catch (error: any) {
      console.error('[progressSync] Failed to queue item:', error);
      return { 
        status: 'failed', 
        error: error.message,
        reason: 'queue_error'
      };
    }
  }
  
  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(progressData),
    });
    
    if (response.ok) {
      const data = await response.json();
      return { status: 'synced', data };
    }
    
    try {
      const id = await queueSyncItem('progress', progressData);
      return { status: 'queued', id, reason: 'api_error' };
    } catch (queueError: any) {
      console.error('[progressSync] Failed to queue item after API error:', queueError);
      return { 
        status: 'failed', 
        error: queueError.message,
        reason: 'queue_error_after_api_fail'
      };
    }
  } catch (error: any) {
    try {
      const id = await queueSyncItem('progress', progressData);
      return { status: 'queued', id, error: error.message };
    } catch (queueError: any) {
      console.error('[progressSync] Failed to queue item after network error:', queueError);
      return { 
        status: 'failed', 
        error: queueError.message,
        reason: 'queue_error_after_network_fail'
      };
    }
  }
}

/**
 * Sync session data
 */
export async function syncSession(sessionData: any): Promise<SaveProgressResult> {
  return saveProgress({ ...sessionData, type: 'session' });
}

/**
 * Sync score update
 */
export async function syncScore(scoreData: any): Promise<SaveProgressResult> {
  return saveProgress({ ...scoreData, type: 'score' });
}

/**
 * Get pending sync count
 */
export async function getPendingCount(): Promise<number> {
  try {
    const queue = await getSyncQueue();
    return queue.length;
  } catch (error) {
    console.error('[progressSync] Failed to get pending count:', error);
    return 0;
  }
}
